import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  getListingBySlug,
  type ListingDetailData,
  type ListingContent,
} from '@/data/listing-details.data';

const fmtMoney = (n: unknown): string => {
  const num = typeof n === 'number' ? n : Number(n);
  if (!num || isNaN(num)) return 'N/A';
  return `$${num.toLocaleString('en-US')}`;
};

const str = (v: unknown, fallback = 'N/A'): string => {
  if (v === undefined || v === null || v === '') return fallback;
  return String(v);
};

const PLACEHOLDER_IMG =
  'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&q=80';

function mapDealToListingContent(deal: any): ListingContent {
  const isConfidential =
    deal.isConfidential === true ||
    deal.confidentialityLevel === 'confidential';

  const title = isConfidential
    ? 'Confidential Business'
    : str(deal.tradeName || deal.legalName || deal.businessName, 'Business Opportunity');

  const city = str(deal.locationCity || deal.city, '');
  const country = str(deal.locationCountry || deal.country, '');
  const location = [city, country].filter(Boolean).join(', ') || 'N/A';

  const detailedInfo: { label: string; value: string }[] = [
    { label: 'Industry', value: str(deal.industry || deal.industrySector) },
    { label: 'Full-Time Employees', value: str(deal.fullTimeEmployees ?? deal.employeesFullTime) },
    { label: 'Location', value: str(city) },
    { label: 'NAICS Code', value: str(deal.naicsCode) },
  ];

  const description: string[] = [
    str(deal.description || deal.businessDescription, 'No description provided.'),
  ];

  // AJOUTÉ : mappe les documents réellement soumis par le vendeur
  // (uploadedDocuments, écrits par submitDeal()) vers la section publique
  // "Available Documents". Gated derrière requiresNda comme les mocks —
  // le lien réel (url) n'est utilisé qu'une fois le NDA signé (voir
  // ListingDetail.tsx). Les documents post-NDA plus poussés (registre
  // d'actifs, Deal Room complet) restent dans DealRoom.tsx (point 4),
  // pas dupliqués ici.
  const documents = Array.isArray(deal.uploadedDocuments)
    ? deal.uploadedDocuments.map((d: any) => ({
        name: d.name || 'Document',
        type: (d.mimeType || d.type || 'PDF').toUpperCase(),
        url: d.url || undefined,
        requiresNda: true,
      }))
    : [];

  return {
    title,
    location,
    badges: [
      deal.sellerFinancingOffered && { label: 'Seller Financing', type: 'green' as const },
      isConfidential && { label: 'Confidential', type: 'gold' as const },
    ].filter(Boolean) as any[],
    mainImage: str(deal.mainImage || deal.imageUrl, PLACEHOLDER_IMG),
    thumbnails: Array.isArray(deal.thumbnails) ? deal.thumbnails : [],
    financials: {
      askingPrice: fmtMoney(deal.askingPrice),
      cashFlow: fmtMoney(deal.cashFlowEquity ?? deal.cashFlow),
      grossRevenue: fmtMoney(deal.revenueYear1 ?? deal.grossRevenue),
      sde: fmtMoney(deal.cashFlowEquity ?? deal.sde),
      ebitda: fmtMoney(deal.ebitdaYear1 ?? deal.ebitda),
      realEstate: fmtMoney(deal.realEstateValue ?? deal.propertyValue),
      established: str(deal.yearEstablished ?? deal.established),
      ffe: fmtMoney(deal.ffeValue ?? deal.ffe),
      inventory: fmtMoney(deal.inventoryValue ?? deal.inventory),
    },
    description,
    detailedInfo,
    financingOptions: [],
    documents,
    isOffMarket: deal.isOffMarket === true,
  };
}

export interface UseRealListingInjectorResult {
  loading: boolean;
  found: boolean;
  listing: ListingDetailData | null;
}

export function useRealListingInjector(slug: string | undefined): UseRealListingInjectorResult {
  const [loading, setLoading] = useState<boolean>(true);
  const [found, setFound] = useState<boolean>(false);
  const [listing, setListing] = useState<ListingDetailData | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!slug) {
        setLoading(false);
        setFound(false);
        setListing(null);
        return;
      }

      // Annonce statique (mock) — aucun appel Firestore nécessaire, on
      // laisse ListingDetail.tsx utiliser directement getListingBySlug().
      if (getListingBySlug(slug)) {
        if (!cancelled) {
          setFound(true);
          setListing(null); // pas d'injection : le mock sera résolu localement
          setLoading(false);
        }
        return;
      }

      try {
        const snap = await getDoc(doc(db, 'dealSubmissions', slug));
        if (!snap.exists()) {
          if (!cancelled) {
            setFound(false);
            setListing(null);
            setLoading(false);
          }
          return;
        }

        const data = snap.data() as any;
        const content = mapDealToListingContent(data);
        const mapped: ListingDetailData = { id: snap.id, slug, en: content, fr: content };

        if (!cancelled) {
          setFound(true);
          setListing(mapped);
          setLoading(false);
        }
      } catch (err) {
        console.error('[useRealListingInjector] Failed to load deal', slug, err);
        if (!cancelled) {
          setFound(false);
          setListing(null);
          setLoading(false);
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { loading, found, listing };
}